import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * 5-Stage Hydroponic Calculation Workflow State Management
 * 
 * Holds what the user has chosen on each screen (water, targets, salts, recipe results) and which season they
 * are working in. It does NOT decide which step is open or confirmed: that lives in the server's recipe draft
 * (see recipeDraftSlice and utils/stepAccess).
 */

// Workflow Stage Definitions
export enum WorkflowStage {
  WATER_PROFILE = 'water-profile',
  CROP_FORMULATION = 'crop-formulation', 
  SALT_MANAGEMENT = 'salt-management',
  STOCK_SOLUTIONS = 'stock-solutions',
  UNIFIED_CALCULATOR = 'unified-calculator'
}

// Water Profile Data
export interface WorkflowWaterProfile {
  id?: string;
  name: string;
  ph: number;
  /** True when no pH was entered and 7 is a stand-in. Not part of what the server stores. */
  phAssumed?: boolean;
  /** The day the water was tested (yyyy-mm-dd), as the user entered it. */
  testedAt?: string;
  ec: number;
  elements: {
    symbol: string;
    concentration: number;
    unit: string;
  }[];
  analysisResults?: {
    qualityScore: number;
    recommendations: string[];
    compatibility: 'excellent' | 'good' | 'fair' | 'poor';
  };
}

// Crop Formulation Data
export interface WorkflowFormulation {
  id?: string;
  name: string;
  cropType?: string; // Made optional to match Formulation interface
  description?: string;
  targetEc?: number; // Added to match Formulation interface
  targetPh?: number; // Added to match Formulation interface
  volume?: number; // Added to match Formulation interface
  volumeUnit?: string; // Added to match Formulation interface
  elements: {
    symbol: string;
    concentration: number;
    unit: string;
  }[];
  analysisResults?: {
    qualityScore: number;
    recommendations: string[];
    compatibility: 'excellent' | 'good' | 'fair' | 'poor';
  };
  scientificReference?: string;
  ratioAnalysis?: {
    npkRatio: string;
    micronutrientBalance: 'optimal' | 'adequate' | 'deficient';
  };
}

// Salt Selection Data
export interface WorkflowSaltSelection {
  selectedSalts: {
    id: string;
    name: string;
    formula: string;
    elements: { symbol: string; percentage: number }[];
    compatibility: 'beneficial' | 'neutral' | 'caution' | 'incompatible';
  }[];
  customSalts: any[]; // User-created salts
  compatibilityMatrix: {
    [saltId: string]: {
      [otherSaltId: string]: 'beneficial' | 'neutral' | 'caution' | 'incompatible';
    };
  };
}

// Stock Solution Configuration
export interface WorkflowStockSolution {
  type: 'single' | 'partA' | 'partB' | 'partAB';
  volume: number;
  volumeUnit: string;
  configuration: {
    partA?: { salts: string[]; volume: number };
    partB?: { salts: string[]; volume: number };
    single?: { salts: string[]; volume: number };
  };
  dilutionRatio: number;
  workingSolutionVolume: number;
  preparationSteps: string[];
}

// Final Calculation Results
export interface WorkflowCalculationResults {
  recipe: {
    salts: { name: string; amount: number; unit: string }[];
    phAdjustment: { product: string; amount: number; unit: string };
    totalCost: number;
    preparationTime: number;
  };
  predictions: {
    finalPh: number | null;
    finalEc: number;
    achievedConcentrations: { symbol: string; target: number; adjustedDemand?: number; achieved: number }[];
    ionicStrength?: number;
    totalDissolvedSolids?: number;
  };
  instructions: {
    stockPreparation: string[];
    workingSolution: string[];
    qualityChecks: string[];
  };
  /**
   * Compatibility field copied from the stage-4 snapshot.
   * The nested error numbers are placeholders. Nothing reads this field to change grams or the report.
   */
  hydroBuddyAnalysis?: {
    errorAnalysis: {
      overallAccuracy: 'excellent' | 'good' | 'fair' | 'poor';
      maxError: number;
      meanAbsoluteError: number;
      rootMeanSquareError: number;
    };
    metadata: {
      matrixCondition: number;
      solverMethod: string;
      calculationTime: number;
      warnings?: string[];
    };
    matrixCondition: number;
    solverMethod: string;
  };
}

// Main Workflow State
export interface WorkflowState {
  currentStage: WorkflowStage;

  // Stage Data
  waterProfile: WorkflowWaterProfile | null;
  formulation: WorkflowFormulation | null;
  saltSelection: WorkflowSaltSelection | null;
  stockSolution: WorkflowStockSolution | null;
  calculationResults: WorkflowCalculationResults | null;

  /** Project, crop, and season this draft will be saved under. */
  activeContext: {
    projectId: string;
    projectName: string;
    cropId: string;
    cropName: string;
    seasonId: string;
    seasonName: string;
    formulationId: string | null;
    formulationName?: string;
    /** The growth stage of the season this draft is for (set when the user starts from the Project page). */
    seasonStageId?: string;
    stageName?: string;
    place?: string;
    latitude?: number;
    longitude?: number;
    area?: number;
    areaUnit?: string;
    suggestedSaltIds?: string[];
    suggestedWaterName?: string;
  } | null;

  /** Present only after the user answers the FlahaX panel. Null leaves Recipe unchanged. */
  flahaxDraft: {
    accepted: boolean;
    salts: { id: string; name: string; gramsPerLitre: number }[];
    rows: { symbol: string; target: number | null; final: number; deltaPct: number | null }[];
  } | null;
  
  // UI State
  loading: boolean;
  error: string | null;
}

// Initial State
const initialState: WorkflowState = {
  currentStage: WorkflowStage.WATER_PROFILE,

  waterProfile: null,
  formulation: null,
  saltSelection: null,
  stockSolution: null,
  calculationResults: null,
  activeContext: null,
  flahaxDraft: null,
  
  loading: false,
  error: null,
};

/** Results are computed from water, targets and salts; when one of those changes they no longer describe it. */
function clearOutputs(state: WorkflowState) {
  state.stockSolution = null;
  state.calculationResults = null;
  state.flahaxDraft = null;
}

function canonicalSalts(value: WorkflowSaltSelection | null) {
  return [...(value?.selectedSalts || [])].sort((x, y) => x.id.localeCompare(y.id));
}

// Workflow Slice
const workflowSlice = createSlice({
  name: 'workflow',
  initialState,
  reducers: {
    setCurrentStage: (state, action: PayloadAction<WorkflowStage>) => {
      state.currentStage = action.payload;
    },

    // Stage Data Actions
    setWaterProfile: (state, action: PayloadAction<WorkflowWaterProfile>) => {
      if (JSON.stringify(state.waterProfile) !== JSON.stringify(action.payload)) clearOutputs(state);
      state.waterProfile = action.payload;
    },
    
    setFormulation: (state, action: PayloadAction<WorkflowFormulation>) => {
      if (JSON.stringify(state.formulation) !== JSON.stringify(action.payload)) clearOutputs(state);
      state.formulation = action.payload;
    },
    
    setSaltSelection: (state, action: PayloadAction<WorkflowSaltSelection>) => {
      if (JSON.stringify(canonicalSalts(state.saltSelection)) !== JSON.stringify(canonicalSalts(action.payload))) {
        clearOutputs(state);
      }
      state.saltSelection = action.payload;
    },

    setStockSolution: (state, action: PayloadAction<WorkflowStockSolution>) => {
      state.stockSolution = action.payload;
    },
    
    setActiveContext: (
      state,
      action: PayloadAction<WorkflowState['activeContext']>
    ) => {
      const before = state.activeContext;
      const after = action.payload;
      if (before?.projectId !== after?.projectId || before?.cropId !== after?.cropId ||
          before?.seasonId !== after?.seasonId || before?.formulationId !== after?.formulationId ||
          before?.seasonStageId !== after?.seasonStageId) {
        return { ...initialState, activeContext: after };
      }
      state.activeContext = action.payload;
    },

    setFlahaxDraft: (
      state,
      action: PayloadAction<WorkflowState['flahaxDraft']>
    ) => {
      state.flahaxDraft = action.payload;
    },

    setCalculationResults: (state, action: PayloadAction<WorkflowCalculationResults>) => {
      state.calculationResults = action.payload;
    },
    
    // UI Actions
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Reset Actions
    resetWorkflow: (state) => {
      return { ...initialState };
    },
  }
});

export const {
  setCurrentStage,
  setWaterProfile,
  setFormulation,
  setSaltSelection,
  setStockSolution,
  setCalculationResults,
  setActiveContext,
  setFlahaxDraft,
  setLoading,
  setError,
  resetWorkflow,
} = workflowSlice.actions;

export default workflowSlice.reducer;
